/**
 * Viagem ativa: quem sou eu nela, o que posso, e quem mais está.
 *
 * Três telas precisavam disso ao mesmo tempo (financeiro, detalhe da despesa,
 * fechar contas), e cada uma buscando por conta própria significaria três
 * versões do "o que eu posso fazer" — que é justamente onde uma diverge.
 *
 * As permissões vêm do servidor e servem só para a UI **ocultar ou marcar**.
 * Quem autoriza é o servidor, em toda requisição.
 *
 * O working set é persistido no IndexedDB e lido de lá primeiro. Sem isso,
 * abrir o app sem rede deixaria as telas sem saber quem participa da viagem —
 * e registrar uma despesa offline, que é um dos casos de uso centrais,
 * simplesmente não funcionaria.
 */
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { loadTrip, saveTrip } from '../features/roteiro/persistence';

export interface MembroDaViagem {
  memberId: string;
  userId: string;
  name: string;
  role: 'owner' | 'admin' | 'collaborator' | 'participant';
  /**
   * Família da pessoa nesta viagem. Ausente enquanto a viagem não tem famílias
   * configuradas — e é essa ausência que desabilita a divisão por família.
   */
  familyId?: string | undefined;
  /** Nome da família, para a divisão por família mostrar cotas com nome. */
  familyName?: string | undefined;
  /** Foto da pessoa (URL assinada de vida curta), quando ela pôs uma. */
  avatarUrl?: string | null | undefined;
  /** Cor da família, para o anel do avatar. */
  familyColor?: string | undefined;
}

/**
 * A viagem como o servidor a descreve.
 *
 * É o que as telas leem para escrever nome, destino, datas e fuso. Não há
 * valor de reserva para nenhum destes campos: uma viagem sem eles não existe,
 * e inventá-los é o defeito que este contexto passou a impedir.
 */
export interface ViagemAtual {
  id: string;
  name: string;
  primaryDestination: string;
  startDate: string;
  endDate: string;
  timezone: string;
  baseCurrency: string;
  /** Já há despesas ou acertos: a moeda não muda mais. */
  currencyLocked: boolean;
  status: 'upcoming' | 'active' | 'finished' | 'archived';
  role: 'owner' | 'admin' | 'collaborator' | 'participant';
  /** Nulos até alguém preencher: viagem nova não tem hospedagem. */
  accommodationName: string | null;
  accommodationAddress: string | null;
  /** Hospedagem como lugar (Places), para o pin do mapa. */
  accommodationPlaceId: string | null;
  accommodationLatitude: number | null;
  accommodationLongitude: number | null;
  accommodationCheckIn: string | null;
  accommodationCheckOut: string | null;
  accommodationNotes: string | null;
  /** Funcionalidades desligadas por quem administra (só chaves com false). */
  features: Record<string, boolean>;
  /** HH:MM (fuso da viagem) em que o Resumo do dia fica pronto. */
  summaryTime?: string;
}

export interface TripContextValue {
  trip: ViagemAtual;
  tripId: string;
  currency: string;
  meuMemberId: string;
  meuUserId: string;
  permissions: string[];
  members: MembroDaViagem[];
  /** `memberId` → nome, para as telas escreverem gente em vez de id. */
  nomes: Record<string, string>;
  carregando: boolean;
  erro: boolean;
}

const Contexto = createContext<TripContextValue | null>(null);

async function ler<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar (${res.status})`);
  return (await res.json()) as T;
}

export function TripProvider({ trip, children }: { trip: ViagemAtual; children: ReactNode }) {
  const tripId = trip.id;
  const currency = trip.baseCurrency;

  // Primeiro o que está no aparelho; a rede refina depois.
  const [local, setLocal] = useState<Awaited<ReturnType<typeof loadTrip>> | undefined>(
    undefined,
  );

  useEffect(() => {
    let vivo = true;
    void loadTrip(tripId).then((dados) => {
      if (vivo) setLocal(dados);
    });
    return () => {
      vivo = false;
    };
  }, [tripId]);

  const eu = useQuery({
    queryKey: ['viagem', 'me', tripId],
    queryFn: () =>
      ler<{
        user: { id: string };
        tripMemberId?: string;
        permissions: string[];
      }>(`/api/v1/trips/${tripId}/me`),
  });

  const membros = useQuery({
    queryKey: ['viagem', 'membros', tripId],
    queryFn: () => ler<{ members: MembroDaViagem[] }>(`/api/v1/trips/${tripId}/members`),
  });

  const daRede =
    eu.data && membros.data
      ? {
          tripId,
          meuMemberId: eu.data.tripMemberId ?? '',
          meuUserId: eu.data.user.id,
          permissions: eu.data.permissions,
          members: membros.data.members,
        }
      : null;

  useEffect(() => {
    if (daRede) void saveTrip(daRede);
    // Só quando a resposta da rede muda de verdade.
  }, [JSON.stringify(daRede)]);

  const efetivo = daRede ?? local ?? null;
  const lista = (efetivo?.members ?? []) as MembroDaViagem[];

  const valor: TripContextValue = {
    trip,
    tripId,
    currency,
    meuMemberId: efetivo?.meuMemberId ?? '',
    meuUserId: efetivo?.meuUserId ?? '',
    permissions: efetivo?.permissions ?? [],
    members: lista,
    nomes: Object.fromEntries(lista.map((m) => [m.memberId, m.name])),
    // `local === undefined` significa que a leitura do IndexedDB ainda não
    // terminou. `null` significa que terminou e não havia nada.
    carregando: efetivo === null && (local === undefined || eu.isLoading || membros.isLoading),
    // Só é erro quando a rede falhou E não há nada no aparelho: quem já abriu
    // a viagem antes continua usando o app offline.
    erro: efetivo === null && local === null && (eu.isError || membros.isError),
  };

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useTrip(): TripContextValue {
  const valor = useContext(Contexto);
  if (!valor) throw new Error('useTrip precisa estar dentro de <TripProvider>.');
  return valor;
}

export function usePermissao(chave: string): boolean {
  return useTrip().permissions.includes(chave);
}
